import { createSlice } from "@reduxjs/toolkit";
// utils
import axiosInstance from "../../api/axiosInstance";
// constants
import { INITIAL_CREATOR_FILTERS } from "../../utils/constants";
import { discoverApi } from "../apiSlices";
import { hasFiltersChangedExcludingSort } from "../../utils/helpers";

// ----------------------------------------------------------------------

const initialState = {
  creatorDetails: null,
  similarCreatorDetails: null,
  selectedContent: null,
  layoutView: "grid", // list or grid
  selectedDetailsTab: "overview", // overview, content, similar-creators
  selectedSimilarCreatorDetailsTab: "overview", // overview, content, similar-creators
  // creators filters, sorting and pagination
  filters: INITIAL_CREATOR_FILTERS,
  // creators api data
  creatorsLoading: false,
  creatorsFetching: false,
  creatorsError: null,
  creatorCount: 0,
  creators: [],
  pagination: {
    currentPage: 1,
    hasNext: null,
    hasPrevious: null,
  },
  // Internal state for filter timeout
  _filterTimeout: null,

  // wishlist
  wishlistCreatorId: null,
};

const slice = createSlice({
  name: "creator",
  initialState,
  reducers: {
    setCreatorDetails(state, action) {
      state.selectedDetailsTab = action.payload?.selectedTab || "overview";
      state.creatorDetails = action.payload;
      if (action.payload === null) {
        state.selectedContent = null;
      }
    },
    setSimilarCreatorDetails(state, action) {
      state.selectedSimilarCreatorDetailsTab =
        action.payload?.selectedTab || "overview";
      state.similarCreatorDetails = action.payload;
      if (action.payload === null) {
        state.selectedContent = null;
      }
    },
    setSelectedContent(state, action) {
      state.selectedContent = action.payload;
    },
    setLayoutView(state, action) {
      state.layoutView = action.payload;
    },
    setSelectedDetailsTab(state, action) {
      state.selectedDetailsTab = action.payload;
    },
    setSelectedSimilarCreatorDetailsTab(state, action) {
      state.selectedSimilarCreatorDetailsTab = action.payload;
    },
    setCreatorsLoading(state, action) {
      state.creatorsLoading = action.payload;
    },
    setCreatorsFetching(state, action) {
      state.creatorsFetching = action.payload;
    },
    setCreatorsError(state, action) {
      state.creatorsError = action.payload;
    },
    setCreatorsDataWithFilters(state, action) {
      // use this action when using filters
      const { creator_count, creators, pagination } = action.payload;
      state.creatorCount = creator_count;
      state.creators = creators;
      state.creatorsError = null;
      state.pagination = {
        currentPage: pagination.current_page,
        hasNext: pagination.has_next,
        hasPrevious: pagination.has_previous,
      };
    },
    setCreatorsDataWithPagination(state, action) {
      // use this action when using pagination
      const { creator_count, creators, pagination } = action.payload;
      state.creatorCount = creator_count;
      state.creators =
        pagination.current_page === 1
          ? creators
          : [...state.creators, ...creators];
      state.creatorsError = null;
      state.pagination = {
        currentPage: pagination.current_page,
        hasNext: pagination.has_next,
        hasPrevious: pagination.has_previous,
      };
    },
    updateCreatorFilters(state, action) {
      state.filters.creator_filters = {
        ...state.filters?.creator_filters,
        ...action.payload,
      };
      state.filters.pagination.page = 1;
      const filtersApplied = hasFiltersChangedExcludingSort(state.filters);

      // Set to relevance if filters applied, otherwise reset to default
      state.filters.sort_by = filtersApplied
        ? [{ field: "relevance", order: "desc" }]
        : INITIAL_CREATOR_FILTERS.sort_by;
    },
    updateProductFilters(state, action) {
      state.filters.product_filters = {
        ...state.filters.product_filters,
        ...action.payload,
      };
      state.filters.pagination.page = 1;
      const filtersApplied = hasFiltersChangedExcludingSort(state.filters);

      // Set to relevance if filters applied, otherwise reset to default
      state.filters.sort_by = filtersApplied
        ? [{ field: "relevance", order: "desc" }]
        : INITIAL_CREATOR_FILTERS.sort_by;
    },
    updateSortBy(state, action) {
      state.filters.sort_by = action.payload;
      state.filters.pagination.page = 1;
    },
    updatePagination(state, action) {
      state.filters.pagination = {
        ...state.filters.pagination,
        ...action.payload,
      };
    },
    resetFilters(state) {
      state.filters = INITIAL_CREATOR_FILTERS;
    },
    _setFilterTimeout(state, action) {
      state._filterTimeout = action.payload;
    },
    setWishlistCreatorId(state, action) {
      state.wishlistCreatorId = action.payload;
    },
  },
});

// Add the timeout field to initial state
initialState._filterTimeout = null;

// Reducer
export default slice.reducer;

// Actions
export const {
  setCreatorDetails,
  setSimilarCreatorDetails,
  setSelectedContent,
  setLayoutView,
  setSelectedDetailsTab,
  setSelectedSimilarCreatorDetailsTab,
  updateCreatorFilters,
  updateProductFilters,
  updateSortBy,
  updatePagination,
  resetFilters,
  _setFilterTimeout,
  setWishlistCreatorId,
} = slice.actions;

// ----------------------------------------------------------------------

export function getCreatorsWithFilters() {
  return async (dispatch, getState) => {
    const { filters } = getState().creator;

    dispatch(slice.actions.setCreatorsLoading(true));
    try {
      const response = await axiosInstance.post(
        "/discoverCreatorsWithFilters",
        {
          ...filters,
        },
      );
      dispatch(slice.actions.setCreatorsDataWithFilters(response.data));
      const category = filters?.creator_filters?.categories?.[0];
      const subcategory = filters?.creator_filters?.subcategories || [];

      if (category || subcategory.length > 0) {
        dispatch(
          discoverApi.endpoints.createRecentFilter.initiate({
            category: category,
            subcategory: subcategory,
            filter_obj: filters,
          }),
        );
      }
      dispatch(slice.actions.setCreatorsLoading(false));
    } catch (error) {
      dispatch(slice.actions.setCreatorsError(error));
    } finally {
      dispatch(slice.actions.setCreatorsLoading(false));
    }
  };
}

// ----------------------------------------------------------------------

export function getCreatorsWithPagination(page = 1) {
  return async (dispatch, getState) => {
    const { filters } = getState().creator;
    const params = {
      ...filters,
      pagination: {
        ...filters.pagination,
        page,
      },
    };
    dispatch(slice.actions.setCreatorsFetching(true));
    try {
      const response = await axiosInstance.post(
        "/discoverCreatorsWithFilters",
        {
          ...params,
        },
      );
      dispatch(slice.actions.setCreatorsDataWithPagination(response.data));
      dispatch(slice.actions.setCreatorsFetching(false));
    } catch (error) {
      dispatch(slice.actions.setCreatorsError(error));
    } finally {
      dispatch(slice.actions.setCreatorsFetching(false));
    }
  };
}
